import React, { useEffect, useState } from 'react';
import {
  Stack, Title, Text, Paper, Group, TextInput, NumberInput, Select, MultiSelect,
  Button, Badge, Loader, SimpleGrid, Code, Switch,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import useAuthenticatedPut from '../../hooks/useAuthenticatedPut';
import config from '../../config';
import DraftBadge from '../common/DraftBadge';

// Nymeria settings = nymeria.variant.config_json (the live agent knobs). The 'default' variant is the
// global config the cockpit recommendation reads; a rep assigned a variant uses theirs.
const SURFACES = ['house', 'nymeria', 'advisor', 'segment', 'part'];
const VERBOSITY = ['terse', 'normal', 'deep'];

function VariantCard({ v, onSaved }) {
  const parse = () => { try { return JSON.parse(v.config_json || '{}') || {}; } catch { return {}; } };
  const [cfg, setCfg] = useState(parse());
  useEffect(() => { setCfg(parse()); }, [v.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const { put } = useAuthenticatedPut();
  const [saving, setSaving] = useState(false);
  const set = (k, val) => setCfg((c) => ({ ...c, [k]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await put(`${config.apiBaseUrl}/api/nymeria/variant/${v.id}`, { configJson: JSON.stringify(cfg) });
      notifications.show({ message: 'AI Sales Agent settings saved.', color: 'green', position: 'bottom-right', autoClose: 2500 });
      onSaved?.();
    } catch (e) {
      notifications.show({ message: `Failed: ${e?.response?.data?.message || e.message}`, color: 'red', position: 'bottom-right', autoClose: 5000 });
    } finally { setSaving(false); }
  };

  const active = v.active === true || v.active === 1;
  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <Title order={4}>{v.name}</Title>
          <Badge color={active ? 'green' : 'gray'} variant="light">{active ? 'active' : 'inactive'}</Badge>
        </Group>
        <Button size="xs" loading={saving} onClick={save}>Save</Button>
      </Group>
      {v.description && <Text size="xs" c="dimmed" mb="sm">{v.description}</Text>}
      <Switch mb="sm"
        label="Recommend only on actionable deals"
        description="The AI Sales Agent's recommendation runs only when the judge says quote / quote-with-review — it skips deprioritized, hold-for-sourcing, or unclear deals (and re-enriching a weak deal won't trigger it). Off = recommend on every deal viewed."
        checked={cfg.gateToActionable !== false}
        onChange={(e) => set('gateToActionable', e.currentTarget.checked)} />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput label="Model" placeholder="gpt-5" value={cfg.model || ''}
          onChange={(e) => set('model', e.currentTarget.value || undefined)}
          description="Overrides the default (gpt-5-mini)." />
        <NumberInput label="Max tool iterations" min={1} max={20} value={cfg.maxIterations ?? ''}
          onChange={(val) => set('maxIterations', Number(val) || undefined)} />
        <Select label="Reasoning verbosity" data={VERBOSITY} value={cfg.reasoningVerbosity || null}
          onChange={(val) => set('reasoningVerbosity', val || undefined)} clearable />
        <NumberInput label="Teaching examples (cap)" min={0} max={30} value={cfg.teachingCap ?? ''}
          onChange={(val) => set('teachingCap', Number(val) || undefined)} />
        <MultiSelect label="Guidance surfaces injected" data={SURFACES} value={cfg.guidanceSurfaces || []}
          onChange={(val) => set('guidanceSurfaces', val.length ? val : undefined)}
          description="Which tiers to inject into the agent's prompt (default: house, AI Sales Agent, advisor)." />
      </SimpleGrid>
      <Code block mt="sm" style={{ fontSize: 11 }}>{JSON.stringify(cfg, null, 2)}</Code>
    </Paper>
  );
}

export default function NymeriaSettings() {
  const { data, isLoading, refetch } = useAuthenticatedData(`${config.apiBaseUrl}/api/nymeria/variants`);
  const variants = Array.isArray(data) ? data : [];
  return (
    <Stack gap="md" p="md">
      <DraftBadge />
      <div>
        <Title order={2}>AI Sales Agent settings</Title>
        <Text size="sm" c="dimmed">
          The live agent knobs — model, context, reasoning depth, and which guidance surfaces the AI Sales Agent
          loads. The <b>default</b> variant is the global config the cockpit recommendation reads; a rep assigned
          a variant uses theirs.
        </Text>
      </div>
      {isLoading && <Group justify="center" p="xl"><Loader /></Group>}
      {variants.map((v) => <VariantCard key={v.id} v={v} onSaved={refetch} />)}
      {!isLoading && variants.length === 0 && <Text c="dimmed">No variants found (apply migration 25).</Text>}
    </Stack>
  );
}
